import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import React, { useEffect, useState } from "react";

// JOB-3346
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
import { handleAuth } from "../../../components/Login/Auth";
import AxiosInstance from "../../AxiosInstance";
import "./style.css";
import sendToast from "../../../components/Toast/sendToast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Grid } from "@mui/material";
import showToast from "../../../components/Toast/Toster";
import { Typography } from "@mui/material";
import WhiteButton from "../../../components/Button/WhiteButton";
import BlueButton from "../../../components/Button/BlueButton";
import { LoaderComponent } from "../../../components/Icon/Index";
import { getItem } from "../../../utils/storage";
import { useUserTimezoneFormatter } from "../../../components/UserTimezoneDateFormatter/UserTimezoneDateFormatter";
import EmailDisabledModal from "../../../components/EmailDisabledModal/EmailDisabledModal";

const InvoiceMail = ({
  modal,
  setModal,
  customerData,
  Total,
  invoiceData,
  taxAmount,
  discountAmount,
  subTotal,
  formik,
  handleSubmit,
  DueDate,
  calculatedTaxes,
  saveInvoiceForEmail,
  tokenDecode: tokenDecodeProp,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const formatDateWithUserTimezone = useUserTimezoneFormatter();
  // JOB-3346: click-to-edit recipient (mirrors QuoteMail / ContractMail).
  // Fall through to `invoiceData.customerData` for the same reason as the
  // sibling dialogs — keeps the email visible regardless of which prop
  // shape was used to open the modal.
  const savedCustomerEmail =
    invoiceData?.customerData?.EmailAddress ||
    customerData?.EmailAddress ||
    "";

  const [displayedEmail, setDisplayedEmail] = useState(savedCustomerEmail);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editEmailValue, setEditEmailValue] = useState("");
  const [emailError, setEmailError] = useState("");
  const { CompanyUrl } = useParams();

  useEffect(() => {
    setDisplayedEmail(savedCustomerEmail);
    setIsEditingEmail(false);
    setEditEmailValue("");
    setEmailError("");
  }, [savedCustomerEmail]);

  const handleStartEditEmail = () => {
    setEditEmailValue(displayedEmail || "");
    setEmailError("");
    setIsEditingEmail(true);
  };

  const handleSaveEditEmail = () => {
    const trimmed = (editEmailValue || "").trim();
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setDisplayedEmail(trimmed);
    setIsEditingEmail(false);
    setEmailError("");
  };

  const handleCancelEditEmail = () => {
    setIsEditingEmail(false);
    setEmailError("");
  };

  const canSubmit = !isEditingEmail && Boolean(displayedEmail);

  const todayDate =
    DueDate &&
    formatDateWithUserTimezone(DueDate, {
      dateFormat: "MMMM DD, YYYY",
      includeSeconds: false,
      excludeTime: true,
    });

  const [data, setData] = useState({});
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await handleAuth(navigate, location);
        setData(res?.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const [loader, setLoader] = useState(false);
  const [currentInvoiceData, setCurrentInvoiceData] = useState(invoiceData);
  const [savedInvoiceId, setSavedInvoiceId] = useState(null);
  
  // Update currentInvoiceData when invoiceData prop changes
  useEffect(() => {
    setCurrentInvoiceData(invoiceData);
    if (invoiceData?.InvoiceId) {
      setSavedInvoiceId(invoiceData.InvoiceId);
    }
  }, [invoiceData]);
  
  const handleSendMail = async () => {
    try {
      setLoader(true);

      let invoiceId = currentInvoiceData?.InvoiceId;
      const isExistingInvoice = !!invoiceId;

      // Always save/update the invoice first before sending email
      if (saveInvoiceForEmail) {
        try {
          const saveResult = await saveInvoiceForEmail();
          
          // Check if save failed
          if (!saveResult?.success) {
            setLoader(false);
            return;
          }
          
          invoiceId = saveResult.invoiceId || invoiceId;
          
          // Update the invoice data with the saved invoice - this ensures "Attach PDF" checkbox appears
          const updatedInvoiceData = saveResult.invoiceData || {
            ...currentInvoiceData,
            InvoiceId: invoiceId,
            CustomerId: currentInvoiceData?.CustomerId || invoiceData?.CustomerId,
            CompanyId: currentInvoiceData?.CompanyId || invoiceData?.CompanyId
          };
          setCurrentInvoiceData(updatedInvoiceData);
          setSavedInvoiceId(invoiceId); // Set saved invoice ID to trigger checkbox display
          
          // Show save success toast immediately (only one toast)
          showToast.success(isExistingInvoice ? "Invoice updated successfully" : "Invoice saved successfully");
          
          // Small delay to ensure save toast is visible before proceeding to send email
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          if (!invoiceId) {
            showToast.error("Failed to save invoice. Invoice ID not found in response.");
            setLoader(false);
            return;
          }
        } catch (saveError) {
          // Don't show error if it's a validation error (already shown in saveInvoiceForEmail)
          const errorMessage = saveError?.response?.data?.message || saveError?.message;
          if (errorMessage && !errorMessage.includes("already exists")) {
            showToast.error(errorMessage || "Failed to save invoice. Please try again.");
          }
          setLoader(false);
          return;
        }
      }

      if (!invoiceId) {
        showToast.error("Invoice ID is missing. Please save the invoice first.");
        setLoader(false);
        return;
      }

      // Get CustomerId from multiple possible sources
      const customerId = 
        customerData?.CustomerId || 
        currentInvoiceData?.CustomerId ||
        invoiceData?.CustomerId || 
        currentInvoiceData?.customer?.CustomerId ||
        invoiceData?.customer?.CustomerId ||
        formik?.CustomerId;

      if (!customerId) {
        showToast.error("Customer ID is missing. Please ensure the invoice has a customer assigned.");
        setLoader(false);
        return;
      }

      const url = `/v1/invoice/send_mail`;
      const object = {
        CustomerId: customerId,
        CompanyUrl: CompanyUrl,
        InvoiceId: invoiceId,
        InvoiceStatus: currentInvoiceData?.Status || invoiceData?.Status,
        CompanyId: currentInvoiceData?.CompanyId || invoiceData?.CompanyId,
        Subject: formik?.Subject !== undefined ? formik.Subject : (currentInvoiceData?.Subject || invoiceData?.Subject || ""),
        InvoiceNumber:
          formik?.InvoiceNumber !== undefined ? formik.InvoiceNumber : (currentInvoiceData?.InvoiceNumber || invoiceData?.InvoiceNumber || ""),
        SubTotal: subTotal !== undefined && subTotal !== null ? subTotal : (currentInvoiceData?.SubTotal || invoiceData?.SubTotal || ""),
        // Use formik values first (even if empty/0), only fallback to invoiceData if formik value is undefined
        Discount: formik?.Discount !== undefined && formik?.Discount !== null ? formik.Discount : (currentInvoiceData?.Discount || invoiceData?.Discount || ""),
        Tax: formik?.Tax !== undefined && formik?.Tax !== null ? formik.Tax : (currentInvoiceData?.Tax || invoiceData?.Tax || ""),
        Total: Total !== undefined && Total !== null ? Total : (currentInvoiceData?.Total || invoiceData?.Total || ""),
        IsSendpdf: !!isPdfChecked,
        SendCopyToMerchant: !!sendCopyToMerchant,
        CalculatedTaxes: calculatedTaxes && calculatedTaxes.length > 0 ? calculatedTaxes : (currentInvoiceData?.CalculatedTaxes || invoiceData?.CalculatedTaxes || []),
        invoiceCode: currentInvoiceData?.invoiceCode || invoiceData?.invoiceCode || "",
        // JOB-3346
        RecipientEmail: displayedEmail || "",
      };
      const response = await AxiosInstance.post(url, object);
      if (response?.data?.statusCode === 200) {
        if (response?.data?.emailNotSent) {
          showToast.warning(response?.data?.message || "No email was sent.");
        } else {
          showToast.success(response?.data?.message || "Email sent successfully");
        }
        // Small delay before closing modal to ensure toast is visible
        await new Promise(resolve => setTimeout(resolve, 500));
        setModal(false);
        
        // Add a longer delay before navigation to ensure both toasts are visible
        setTimeout(() => {
          // After sending email, navigate back to invoice list
          // Check for worker role - check both tokenDecode and workerToken
          const tokenDecode = JSON.parse(localStorage.getItem("tokenDecode") || "{}");
          const workerToken = getItem("workerToken");
          
          // Determine if user is a worker - check multiple sources
          const isWorker = tokenDecode?.Role === "Worker" || 
                          tokenDecode?.role === "Worker" || 
                          !!workerToken ||
                          tokenDecode?.Role === "worker" ||
                          tokenDecode?.role === "worker";
          
          // Determine the correct navigation path based on role
          const invoicePath = isWorker
            ? `${CompanyUrl}/staff-member/worker-invoice`
            : `${CompanyUrl}/invoice`;
          
          // Set correct fallback navigats based on role
          const fallbackNavigats = isWorker
            ? ["/index", `/${CompanyUrl}/staff-member/worker-invoice`]
            : ["/index", `/${CompanyUrl}/invoice`];
          
          // Filter out contract paths from navigation history to prevent going to contract table
          const filteredNavigats = location?.state?.navigats
            ?.filter((item) => !item.includes("/contract") && !item.includes("/quote"))
            ?.slice(0, -1) || fallbackNavigats;
          
          navigate(
            `/${invoicePath}`,
            {
              replace: true,
              state: {
                navigats: filteredNavigats,
              },
            }
          );
        }, 2500); // Wait 2.5 seconds so user can see both toasts
      } else if (response?.data?.statusCode === 209) {
        const tokenDecode = tokenDecodeProp || JSON.parse(localStorage.getItem("tokenDecode") || "{}");
        const userRole = tokenDecode?.data?.Role ?? tokenDecode?.Role;
        if ((response?.data?.reason === "email_disabled" || response?.data?.reason === "not_configured") && userRole === "Company") {
          setModal(false);
          setEmailDisabledMessage(response?.data?.message || "Email is not configured or disabled for this company.");
          setEmailDisabledModalOpen(true);
        } else {
          showToast.warning(response?.data?.message || "You have reached the email limit");
        }
      } else {
        sendToast(response?.data?.message);
      }
    } catch (error) {
      if (error?.response?.status === 209 || error?.response?.data?.statusCode === 209) {
        const tokenDecode = tokenDecodeProp || JSON.parse(localStorage.getItem("tokenDecode") || "{}");
        const userRole = tokenDecode?.data?.Role ?? tokenDecode?.Role;
        if ((error?.response?.data?.reason === "email_disabled" || error?.response?.data?.reason === "not_configured") && userRole === "Company") {
          setModal(false);
          setEmailDisabledMessage(error?.response?.data?.message || "Email is not configured or disabled for this company.");
          setEmailDisabledModalOpen(true);
        } else {
          showToast.warning(error?.response?.data?.message || "You have reached the email limit");
        }
      } else {
        console.error(error?.message);
        showToast.error(error?.response?.data?.message || "Failed to send email");
      }
    } finally {
      setLoader(false);
    }
  };

  const [isPdfChecked, setIsPdfChecked] = useState(true); // Default to checked - PDF will be attached by default
  const [sendCopyToMerchant, setSendCopyToMerchant] = useState(false);
  const [emailDisabledModalOpen, setEmailDisabledModalOpen] = useState(false);
  const [emailDisabledMessage, setEmailDisabledMessage] = useState("");
  const theme = useTheme();
  const isDarkDialog = theme.palette.mode === "dark";

  const handleCheckboxChange = (event) => {
    setIsPdfChecked(event.target.checked);
  };

  const handleMerchantCopyChange = (event) => {
    setSendCopyToMerchant(event.target.checked);
  };

  return (
    <>
      <Dialog
        open={modal}
        onClose={() => setModal(!modal)}
        style={{ height: "100%" }}
        fullWidth={true}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            bgcolor: "background.paper",
            backgroundImage: "none",
            boxShadow: isDarkDialog ? 12 : "0px 8px 20px rgba(0, 0, 0, 0.15)",
          },
        }}
        className="email-quote email_sendModel"
      >
        <DialogTitle
          className="d-flex justify-content-between align-items-center"
          sx={{
            bgcolor: "background.paper",
            paddingBottom: "12px",
            borderBottom: "3px solid",
            borderColor: isDarkDialog ? "divider" : "var(--border-orange-color)",
          }}
        >
          <Grid className="Title" style={{ margin: 0, padding: 0 }}>
            <Typography
              sx={{
                color: "text.primary",
                fontWeight: 700,
                fontSize: "20px",
                lineHeight: 1.3,
              }}
            >
              Please Confirm
            </Typography>
          </Grid>
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => setModal(false)}
            sx={{
              color: "text.secondary",
              "& .MuiSvgIcon-root": { color: "inherit" },
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          className="w-100 row mt-3"
          sx={{ bgcolor: "background.paper", color: "text.primary" }}
        >
          <Typography
            component="div"
            sx={{
              fontSize: "16px",
              lineHeight: 1.6,
              color: "text.primary",
              textAlign: "center",
              width: "100%",
            }}
          >
            Are you sure you want to send the{" "}
            <strong style={{ fontWeight: 700 }}>mail</strong>
          </Typography>
          {/* JOB-3346: click-to-edit recipient. */}
          {!isEditingEmail ? (
            <Box
              sx={{
                width: "100%",
                marginTop: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: "16px",
                  lineHeight: 1.6,
                  color: "text.primary",
                }}
              >
                to:{" "}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: "warning.light",
                    fontSize: "18px",
                  }}
                >
                  {displayedEmail || "(no email)"}
                </Box>
              </Typography>
              <Button
                size="small"
                onClick={handleStartEditEmail}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  minWidth: "auto",
                  padding: "2px 10px",
                  color: isDarkDialog ? "#90caf9" : "#063164",
                  "&:hover": {
                    backgroundColor: isDarkDialog
                      ? "rgba(144, 202, 249, 0.08)"
                      : "rgba(6, 49, 100, 0.06)",
                  },
                }}
              >
                Change
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                marginTop: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box sx={{ position: "relative", width: "100%", maxWidth: 360 }}>
                <EmailOutlinedIcon
                  sx={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: isDarkDialog ? "#9aa0a6" : "rgba(15, 43, 70, 0.55)",
                    fontSize: 20,
                    pointerEvents: "none",
                  }}
                />
                <input
                  type="email"
                  autoFocus
                  value={editEmailValue}
                  onChange={(e) => {
                    setEditEmailValue(e.target.value);
                    if (emailError) setEmailError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveEditEmail();
                    if (e.key === "Escape") handleCancelEditEmail();
                  }}
                  placeholder="Enter recipient email"
                  aria-label="Recipient email"
                  style={{
                    width: "100%",
                    padding: "10px 12px 10px 40px",
                    fontSize: 14,
                    borderRadius: 8,
                    border: `1px solid ${
                      emailError
                        ? "var(--red, #dc2626)"
                        : isDarkDialog
                        ? "#3d4f66"
                        : "#CEDAE8"
                    }`,
                    background: isDarkDialog ? "#151d2a" : "#F4F8FD",
                    color: isDarkDialog ? "#e8eaed" : "#0f2b46",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                {emailError && (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "var(--red, #dc2626)",
                      marginTop: "6px",
                      paddingLeft: "4px",
                    }}
                  >
                    {emailError}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveEditEmail}
                  sx={{
                    textTransform: "none",
                    fontSize: 13,
                    fontWeight: 600,
                    minHeight: 32,
                    paddingX: 2,
                    backgroundColor: isDarkDialog ? "#2563eb" : "#063164",
                    "&:hover": {
                      backgroundColor: isDarkDialog ? "#1e54d8" : "#0a3a72",
                    },
                  }}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleCancelEditEmail}
                  sx={{
                    textTransform: "none",
                    fontSize: 13,
                    fontWeight: 600,
                    minHeight: 32,
                    paddingX: 2,
                    borderColor: isDarkDialog ? "#3d4f66" : "#063164",
                    color: isDarkDialog ? "#e8eaed" : "#063164",
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
          <Typography
            sx={{
              fontSize: "14px",
              color: "text.secondary",
              marginTop: "12px",
              display: "block",
              textAlign: "center",
              width: "100%",
            }}
          >
            Note: Ensure the email address is correct before proceeding.
          </Typography>

          <Grid className="order-3 order-lf-3" style={{ width: "100%" }}>
            <hr
              style={{
                borderColor: isDarkDialog ? "#2d3b4e" : "#E8ECF0",
                margin: "20px 0",
              }}
            />
            {/* JOB-3346: keep buttons visible during send; spinner inside submit. */}
            <Grid
              className="d-flex justify-content-between QUoteSendCancelBtn"
              style={{
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "16px",
                flexWrap: "wrap",
                width: "100%",
              }}
            >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    flex: "1 1 auto",
                    minWidth: 0,
                  }}
                >
                  <Typography
                    component="label"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      fontSize: "14px",
                      color: "text.primary",
                      fontWeight: 600,
                      m: 0,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isPdfChecked}
                      onChange={handleCheckboxChange}
                      style={{ marginRight: "8px", cursor: "pointer" }}
                    />
                    Attach PDF
                  </Typography>
                  <Typography
                    component="label"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      fontSize: "14px",
                      color: "text.primary",
                      fontWeight: 600,
                      m: 0,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={sendCopyToMerchant}
                      onChange={handleMerchantCopyChange}
                      style={{ marginRight: "8px", cursor: "pointer" }}
                    />
                    Send a copy to company email
                  </Typography>
                </div>

                <div
                  className="contract-mail-actions"
                  style={{
                    display: "flex",
                    gap: "10px",
                    flexShrink: 0,
                    alignItems: "stretch",
                    marginLeft: "auto",
                  }}
                >
                  <WhiteButton
                    onClick={() => setModal(false)}
                    disabled={loader}
                    label="No, Go Back"
                    style={{
                      fontSize: "14px",
                      color: isDarkDialog ? "#e8eaed" : "#063164",
                      border: isDarkDialog
                        ? "1px solid #3d4f66"
                        : "1px solid #063164",
                      background: isDarkDialog ? "#2d3b4e" : "#fff",
                      textTransform: "none",
                      boxShadow: "none",
                      whiteSpace: "nowrap",
                      borderRadius: "8px",
                      boxSizing: "border-box",
                      minHeight: "44px",
                      paddingLeft: "18px",
                      paddingRight: "18px",
                      opacity: loader ? 0.55 : 1,
                      cursor: loader ? "not-allowed" : "pointer",
                    }}
                    className="ButtomWithN"
                  />
                  <BlueButton
                    onClick={handleSendMail}
                    disabled={!canSubmit || loader}
                    style={{
                      fontSize: "14px",
                      color: "#fff",
                      textTransform: "none",
                      marginLeft: 0,
                      backgroundColor: isDarkDialog ? "#2563eb" : "#002D5B",
                      border: "none",
                      boxShadow: "0px 8px 12px rgba(0, 0, 0, 0.15)",
                      whiteSpace: "nowrap",
                      borderRadius: "8px",
                      boxSizing: "border-box",
                      minHeight: "44px",
                      paddingLeft: "18px",
                      paddingRight: "18px",
                      opacity: canSubmit && !loader ? 1 : 0.55,
                      cursor: canSubmit && !loader ? "pointer" : "not-allowed",
                    }}
                    label={
                      loader ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress
                            size={16}
                            thickness={5}
                            sx={{ color: "#fff" }}
                          />
                          <span>Sending…</span>
                        </Box>
                      ) : (
                        "Yes, Send Email"
                      )
                    }
                    className="yesSnedmailQuote"
                  />
                </div>
              </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <EmailDisabledModal
        open={emailDisabledModalOpen}
        onClose={() => setEmailDisabledModalOpen(false)}
        message={emailDisabledMessage}
      />
    </>
  );
};

export default InvoiceMail;
